import { Component, type ReactNode } from "react";
import { reportError } from "./report";
import { captureException } from "../analytics/posthog";

type Props = {
  children: ReactNode;
  name: string;
  resetKey?: string;
  onError?(): void;
};
/** Keep render/lifecycle failures inside the smallest usable workspace region. */
export class ErrorBoundary extends Component<
  Props,
  { failed: boolean; resetKey?: string }
> {
  state = { failed: false, resetKey: this.props.resetKey };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  static getDerivedStateFromProps(props: Props, state: { resetKey?: string }) {
    return props.resetKey !== state.resetKey
      ? { failed: false, resetKey: props.resetKey }
      : null;
  }
  componentDidCatch(error: unknown) {
    // React does not rethrow boundary errors to window.onerror.
    captureException(error);
    reportError(error);
    this.props.onError?.();
  }
  render() {
    if (!this.state.failed) return this.props.children;
    if (this.props.onError) return null;
    return (
      <section
        className="error-recovery"
        aria-label={`${this.props.name} recovery`}
      >
        <p>{this.props.name} is temporarily unavailable.</p>
        <button onClick={() => this.setState({ failed: false })}>
          Try again
        </button>
      </section>
    );
  }
}
